/**
 * Global network error handling utilities
 * Addresses issue #1531 - recurring "Failed to fetch" errors
 */

import { leaveBreadcrumb, notifyError } from './monitoring.js';
import { t } from 'src/i18n';
import { displayAlert } from './display-alert.js';
import { isIgnorableNetworkError } from './network-error-patterns.js';

// Re-exported for backwards compatibility with existing importers.
export { NETWORK_ERROR_PATTERNS, isIgnorableNetworkError } from './network-error-patterns.js';

// Track critical requests that should show user notifications
const criticalRequests = new WeakSet<Promise<any>>();

/**
 * Mark a promise as critical (should notify user on failure)
 */
export function markAsCritical<T>(promise: Promise<T>): Promise<T> {
  criticalRequests.add(promise);
  return promise;
}

/**
 * Check if a promise was marked as critical
 */
export function isCriticalRequest(promise: Promise<any>): boolean {
  return criticalRequests.has(promise);
}

/**
 * Network state tracking
 */
class NetworkState {
  private _isOnline: boolean = navigator.onLine;
  private _hasShownOfflineMessage: boolean = false;
  private _offlineTimer: number | null = null;

  get isOnline(): boolean {
    return this._isOnline;
  }

  setOnline(): void {
    const wasOffline = !this._isOnline;
    this._isOnline = true;

    if (wasOffline && this._hasShownOfflineMessage) {
      // Clear any pending offline message
      if (this._offlineTimer) {
        clearTimeout(this._offlineTimer);
        this._offlineTimer = null;
      }

      // Don't show alert - offline indicator will handle UI update
      console.log('[Network] Connection restored');
      this._hasShownOfflineMessage = false;
    }
  }

  setOffline(): void {
    this._isOnline = false;

    // Don't show alert - offline indicator will handle UI update
    if (!this._hasShownOfflineMessage) {
      console.log('[Network] Connection lost');
      this._hasShownOfflineMessage = true;
    }
  }
}

export const networkState = new NetworkState();

/**
 * Setup global error handlers for network issues
 */
export function setupNetworkErrorHandlers(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = event.reason;

    // Check if this is a network error
    if (isIgnorableNetworkError(error)) {
      // Log to console for debugging
      console.warn('[Network Error - Ignored]:', error);

      // Prevent default error handling and monitoring reporting
      event.preventDefault();

      // Show user notification if this was a critical request
      if (isCriticalRequest(event.promise)) {
        displayAlert(t('text.couldNotCompleteAction'));
      }

      // Track network issues for patterns
      leaveBreadcrumb('Network error suppressed', {
        errorMessage: error?.message,
        url: error?.url || 'unknown',
      });

      return;
    }

    // For non-network errors, report them with some added context
    if (error && typeof error === 'object') {
      notifyError(error, {
        metadata: {
          promise: {
            rejected: true,
            online: navigator.onLine,
          },
        },
      });
    }
  });

  // Handle online/offline events
  window.addEventListener('online', () => {
    console.log('[Network] Connection restored');
    networkState.setOnline();

    // Dispatch custom event that components can listen to
    window.dispatchEvent(new CustomEvent('network-online'));
  });

  window.addEventListener('offline', () => {
    console.log('[Network] Connection lost');
    networkState.setOffline();

    // Dispatch custom event that components can listen to
    window.dispatchEvent(new CustomEvent('network-offline'));
  });

  // Handle fetch errors globally (for browsers that support it)
  if ('addEventListener' in window) {
    window.addEventListener('error', (event: ErrorEvent) => {
      if (isIgnorableNetworkError(event.error)) {
        console.warn('[Network Error - From error event]:', event.error);
        event.preventDefault();
      }
    });
  }

  console.log('[Network Error Handler] Initialized');
}

/**
 * Utility to wrap async operations with better error handling
 */
export async function withNetworkErrorHandling<T>(
  operation: () => Promise<T>,
  options: {
    critical?: boolean;
    fallback?: T;
    errorMessage?: string;
  } = {},
): Promise<T> {
  try {
    const promise = operation();

    if (options.critical) {
      markAsCritical(promise);
    }

    return await promise;
  } catch (error) {
    if (isIgnorableNetworkError(error)) {
      if (options.errorMessage) {
        displayAlert(options.errorMessage);
      }

      if (options.fallback !== undefined) {
        return options.fallback;
      }
    }

    throw error;
  }
}
